import {
  RadialBarChart,
  RadialBar,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useEffect, useState } from "react";

type CardData = {
  status: string;
  value: number;
  backgroundColor: string;
  fontColor: string;
  imageUrl: string;
};

type MultipleRadialBarsProps = {
  cardsData: CardData[];
  loading?: boolean;
  size:number
  cardContainerRef?:any
  isSplitScreen:boolean
  outerRadius:any
  cardHeight?:any
};

const CustomLegendVertical = ({
  data,
  onLegendClick,
  activeLegends,
}: {
  data: CardData[];
  onLegendClick: (status: string) => void;
  activeLegends: string[];
}) => {
  return (
    <div className="flex flex-col items-center justify-center space-y-4 mt-6">
      {data.map((item, index) => (
        <div
          key={index}
          className="flex items-center space-x-4 cursor-pointer"
          onClick={() => onLegendClick(item.status)}
        >
          <div
            className={`w-4 h-4 rounded-full ${
              !activeLegends.includes(item.status) ? "" : "border-2"
            }`}
            style={{ backgroundColor: item.fontColor }}
          ></div>
          <span
            className={`text-xl font-medium ${
              !activeLegends.includes(item.status)
                ? "text-black"
                : "text-gray-500"
            } min-w-[100px] text-left`}
          >
            {item.status}
          </span>
          <span
            className={`text-xl font-bold ${
              !activeLegends.includes(item.status)
                ? "text-black"
                : "text-gray-700"
            } text-right min-w-[50px]`}
          >
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: any[];
}) => {
  if (
    active &&
    payload &&
    payload.length > 0 &&
    payload[0].payload.name !== "Total"
  ) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border rounded-md p-2 shadow-md">
        <p className="text-sm font-semibold text-gray-800 mb-2">{data.name}</p>
        <p className="text-sm text-gray-600">Value: {data.value}</p>
      </div>
    );
  }
  return null;
};

export const MultipleRadialBars = ({
  cardsData,
  loading,
  size,
  cardContainerRef,
  isSplitScreen,
  outerRadius,
  cardHeight
}: MultipleRadialBarsProps) => {
  useEffect(()=>{
    console.log("labels", cardsData)
  },[cardsData])
  const [activeLegends, setActiveLegends] = useState<string[]>([]);
  const [hoveredStatus, setHoveredStatus] = useState<string | null>(null);

  if (loading || !cardsData) {
    return (
      <div className="bg-[#F9FAFB] shadow-md rounded-2xl p-4 w-full h-full space-y-6">
        {/* Title Skeleton */}
        <Skeleton height={30} width="30%" className="mt-2" />

        {/* Chart Area Skeleton */}
        <div className="flex justify-center items-center h-[300px]">
          <Skeleton circle width={200} height={200} />
        </div>

        {/* Legend Skeleton */}
        <div className="flex flex-col items-center justify-center space-y-4">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="flex items-center space-x-4 w-full max-w-[300px]"
            >
              <Skeleton circle width={16} height={16} />
              <Skeleton width="50%" height={20} />
              <Skeleton width={40} height={20} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  const nonTotalData = cardsData.filter((item) => item.status !== "Total");

  if (nonTotalData.length === 0) {
    return (
      <div className="bg-[#F9FAFB] shadow-md rounded-2xl p-4 w-full h-full flex items-center justify-center">
        <p className="text-2xl text-gray-500 font-medium">No Data Found</p>
      </div>
    );
  }
  const totalItem = cardsData.find((item) => item.status === "Total");
  const baseTotal = totalItem?.value || 100;
  const filteredAndSortedData = cardsData
    .filter((item) => item.status !== "Total")
    .sort((a, b) => a.value - b.value);
  const totalBars = filteredAndSortedData.length;

  const dynamicBars = [
    {
      name: "Total",
      value: baseTotal,
      fill: "transparent",
      innerRadius: "30%",
      outerRadius: "30%", // make it invisible
      stroke: "transparent",
      strokeWidth: 0,
    },
    ...filteredAndSortedData
      .filter(
        (item) =>
          activeLegends.length === 0 || !activeLegends.includes(item.status)
      )
      .map((item, index) => {
        const minRadius = 30;
        const maxRadius = 80;
        const step = (maxRadius - minRadius) / totalBars;
        const baseInnerRadius = minRadius + step * index;
        const baseOuterRadius = minRadius + step * (index + 1);

        return {
          name: item.status,
          value: item.value,
          fill:
            hoveredStatus === item.status
              ? `${item.fontColor}99`
              : `${item.fontColor}`,
          innerRadius: `${baseInnerRadius}%`,
          outerRadius: `${baseOuterRadius}%`,
          stroke: hoveredStatus === item.status ? "transparent" : "transparent",
          strokeWidth: hoveredStatus === item.status ? 2 : 0,
        };
      }),
  ];

  const onLegendClick = (status: string) => {
    setActiveLegends((prevLegends) => {
      if (prevLegends.includes(status)) {
        // Remove from active legends (unselect)
        return prevLegends.filter((item) => item !== status);
      } else {
        // Keep all selected by default, remove only the clicked one
        return [...prevLegends, status];
      }
    });
  };

  return (
    <div className={`bg-[#F9FAFB] shadow-md rounded-2xl p-4 w-full  ${cardHeight ? "h-[250px] mt-[3.5rem] mb-[5rem]" : "" }`} >
      <h2 className="text-3xl font-semibold mt-2">{isSplitScreen ? "Defect Type": "Process Count"}</h2>
      <div className= {isSplitScreen ? "flex pt-[2rem]" : ""}>
        <ResponsiveContainer width="100%" height={size}>
        <RadialBarChart
          startAngle={0}
          endAngle={-360}
          cx="50%"
          cy="50%"
          innerRadius="20%"
          outerRadius={outerRadius}
          barSize={10}
          data={dynamicBars}
        >
          <RadialBar
            background
            dataKey="value"
            cornerRadius={3}
            activeShape={false}
            onMouseEnter={(data: any) => setHoveredStatus(data.name)}
            onMouseLeave={() => setHoveredStatus(null)}
          />
          <Tooltip content={<CustomTooltip />} cursor={false} />
        </RadialBarChart>
      </ResponsiveContainer>
      <CustomLegendVertical
        data={cardsData.filter((item) => item.status !== "Total")}
        onLegendClick={onLegendClick}
        activeLegends={activeLegends}
      />
      </div>
    </div>
  );
};

export default MultipleRadialBars;